import type { ProfileConfig } from "@worker/config-schema";
import { isCoarsePointer, prefersReducedMotion } from "../lib/dom";

/**
 * Particle trail following the cursor. Spawns small dots at a throttled rate
 * on pointer movement; each particle fades and drifts upward over ~700ms.
 *
 * Disabled on touch devices (no cursor) and when reduced-motion is preferred
 * and the effect is listed in motion.reducedMotionSuppresses.
 */
export function attachCursorTrail(
  cfg: ProfileConfig["cursor"]["particleTrail"],
  motion: ProfileConfig["motion"],
): () => void {
  if (!cfg.enabled || isCoarsePointer()) return () => {};
  if (
    motion.respectReducedMotion &&
    motion.reducedMotionSuppresses.includes("cursorTrail") &&
    prefersReducedMotion()
  ) {
    return () => {};
  }

  const speedMul = motion.speedOverrides["cursorTrail"] ?? 1;
  if (speedMul === 0) return () => {};
  const density = Math.max(1, Math.round(cfg.density * speedMul));
  // Minimum ms between particles.
  const throttleMs = Math.max(16, 1000 / density);

  const layer = document.createElement("div");
  layer.className = "cursor-trail";
  layer.setAttribute("aria-hidden", "true");
  document.body.append(layer);

  // Trail style switch — each variant renders differently. Kept as a
  // single listener (spawns a DOM node + kicks off a CSS animation) so
  // we can add more styles later by wiring new cases.
  let lastSpawn = 0;
  const style = cfg.style ?? "particles";
  const lifetimeMs = style === "comet" ? 1200 : style === "ink" ? 1400 : 800;

  const onMove = (ev: PointerEvent) => {
    const now = performance.now();
    if (now - lastSpawn < throttleMs) return;
    lastSpawn = now;

    const p = document.createElement("span");
    p.className = `cursor-trail__particle cursor-trail__particle--${style}`;
    p.style.left = `${ev.clientX}px`;
    p.style.top = `${ev.clientY}px`;
    p.style.setProperty("--tint", cfg.color);

    switch (style) {
      case "sparkle": {
        // 4-pointed star rotation + fade; no drift.
        p.style.setProperty("--dx", "0px");
        p.style.setProperty("--dy", "0px");
        p.style.setProperty("--rot", `${Math.random() * 360}deg`);
        break;
      }
      case "bubble": {
        // Float upward with wobble, expand slightly as it rises.
        p.style.setProperty("--dx", `${(Math.random() - 0.5) * 40}px`);
        p.style.setProperty("--dy", `${-40 - Math.random() * 40}px`);
        break;
      }
      case "ink": {
        // Heavy blob that spreads and fades slowly.
        p.style.setProperty("--dx", `${(Math.random() - 0.5) * 12}px`);
        p.style.setProperty("--dy", `${(Math.random() - 0.5) * 12}px`);
        p.style.setProperty("--size", `${8 + Math.random() * 10}px`);
        break;
      }
      case "comet": {
        // Bright streak with extended tail direction based on last move.
        p.style.setProperty("--dx", `${(Math.random() - 0.5) * 80}px`);
        p.style.setProperty("--dy", `${(Math.random() - 0.5) * 40 - 10}px`);
        break;
      }
      case "emoji": {
        p.textContent = cfg.emoji ?? "✨";
        p.style.setProperty("--dx", `${(Math.random() - 0.5) * 30}px`);
        p.style.setProperty("--dy", `${-30 - Math.random() * 25}px`);
        p.style.setProperty("--rot", `${(Math.random() - 0.5) * 60}deg`);
        break;
      }
      default: {
        // Classic particles.
        p.style.setProperty("--dx", `${(Math.random() - 0.5) * 30}px`);
        p.style.setProperty("--dy", `${-20 - Math.random() * 20}px`);
      }
    }
    layer.append(p);
    window.setTimeout(() => p.remove(), lifetimeMs);
  };

  window.addEventListener("pointermove", onMove, { passive: true });
  return () => {
    window.removeEventListener("pointermove", onMove);
    layer.remove();
  };
}
